import { Component, DestroyRef, inject, OnInit, signal, computed } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { formatDate } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToastService } from '../../../ui';
import { ContractsService, CancellationReason } from '../contracts.service';
import { AuthService } from '../../../core/auth/auth.service';
import { LoggingService } from '../../../core/services/logging.service';
import { defaultEndDate, defaultStartDate } from '../../../shared/utils/invoice-day.util';
import {
  atLeastMinDate,
  cancellationMinDate,
  contractChangeDateOutcome,
  contractChangeMinDate,
  maxInvoiceEndDate,
} from '../../../shared/utils/contract-change-date.util';
import { minDateValidator } from '../../../shared/validators/min-date.validator';
import { currentOrLatestRevision } from '../current-revision.util';
import { Invoice, InvoiceService } from '../../invoice/invoice.service';
import { catchError, map, of } from 'rxjs';
import {
  HLM_DIALOG_SHELL,
  HLM_FORM_FIELD,
  HlmBtnDirective,
  HlmBadgeComponent,
  HlmIconComponent,
  HlmInputDirective,
  HlmTextareaDirective,
  HlmCheckboxComponent,
  HlmRadioGroupComponent,
  HlmRadioComponent,
  HlmSpinnerComponent,
  HlmAlertComponent,
} from '../../../ui';

export interface CancelCustomerDialogData {
  customerId: number;
  invoiceDay: number;
  /** `customer.startDate` — a cancellation may be back-dated a day, but never before it. */
  customerStartDate?: string | null;
  /** Main contract facts for the Send-to-Basic mode (it is a plan change in old CMT terms). */
  mainContractStartDate?: string | null;
  mainContractReactivatedStartDate?: string | null;
  mainContractPlanLevel?: number | null;
  /**
   * Main contract id — enables the "Send to Basic" dual mode (EB-4005). When
   * present, the dialog offers downgrading the customer to the Basic plan of the
   * main contract's current price list instead of cancelling. Omitted → cancel only.
   */
  mainContractId?: number | null;
}

@Component({
  selector: 'cmt-cancel-customer-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    ...HLM_DIALOG_SHELL, ...HLM_FORM_FIELD,
    HlmBtnDirective, HlmBadgeComponent, HlmIconComponent, HlmInputDirective, HlmTextareaDirective,
    HlmCheckboxComponent, HlmRadioGroupComponent, HlmRadioComponent,
    HlmSpinnerComponent, HlmAlertComponent,
  ],
  template: `
    <hlm-dialog-surface size="lg">
      <hlm-dialog-header>
        <hlm-dialog-icon-badge tone="destructive">
          <hlm-icon [name]="mode() === 'basic' ? 'lucideArrowDown' : 'lucideTriangleAlert'" size="1.1rem" />
        </hlm-dialog-icon-badge>
        <div hlmDialogHeading>
          <h2 hlmDialogTitle>{{ mode() === 'basic' ? 'Send to Basic' : 'Cancel Customer' }}</h2>
          <p hlmDialogDescription>
            Customer <span class="font-mono-feat">{{ data.customerId }}</span> —
            {{ mode() === 'basic'
                ? 'downgrade to Basic instead of cancelling.'
                : 'all contracts will be ended.' }}
          </p>
        </div>
        <button hlmDialogClose (click)="dialogRef.close()">
          <hlm-icon name="lucideX" size="1rem" />
        </button>
      </hlm-dialog-header>

      <section hlmDialogBody>
        <!-- EB-4005: dual mode — Cancel vs Send to Basic (only when a main contract is known).
             Presented as two choice cards with Send-to-Basic recommended + why. -->
        @if (canSendToBasic) {
          <div class="mb-5 flex flex-col gap-2" role="radiogroup" aria-label="How to end the customer">
            <!-- Recommended: Send to Basic -->
            <button
              type="button"
              role="radio"
              [attr.aria-checked]="mode() === 'basic'"
              (click)="setMode('basic')"
              [class]="'flex items-start gap-3 rounded-xl border p-3 text-left transition-colors ' +
                (mode() === 'basic' ? 'border-success bg-success/5' : 'border-border hover:bg-muted/40')"
            >
              <span
                class="mt-0.5 grid h-4 w-4 flex-none place-items-center rounded-full border-2"
                [class]="mode() === 'basic' ? 'border-success' : 'border-muted-foreground/40'"
              >
                @if (mode() === 'basic') { <span class="h-1.5 w-1.5 rounded-full bg-success"></span> }
              </span>
              <span class="flex min-w-0 flex-col gap-1">
                <span class="flex items-center gap-2">
                  <span class="font-medium text-foreground">Send to Basic</span>
                  <hlm-badge variant="secondary">Recommended</hlm-badge>
                </span>
                <span class="text-sm text-muted-foreground">
                  No downsides for the customer, and it lets us reactivate their contract with a
                  single click later — instead of cancelling outright.
                </span>
              </span>
            </button>

            <!-- Cancel customer -->
            <button
              type="button"
              role="radio"
              [attr.aria-checked]="mode() === 'cancel'"
              (click)="setMode('cancel')"
              [class]="'flex items-start gap-3 rounded-xl border p-3 text-left transition-colors ' +
                (mode() === 'cancel' ? 'border-success bg-success/5' : 'border-border hover:bg-muted/40')"
            >
              <span
                class="mt-0.5 grid h-4 w-4 flex-none place-items-center rounded-full border-2"
                [class]="mode() === 'cancel' ? 'border-success' : 'border-muted-foreground/40'"
              >
                @if (mode() === 'cancel') { <span class="h-1.5 w-1.5 rounded-full bg-success"></span> }
              </span>
              <span class="flex min-w-0 flex-col gap-1">
                <span class="font-medium text-foreground">Cancel customer</span>
                <span class="text-sm text-muted-foreground">Ends all of the customer's contracts.</span>
              </span>
            </button>
          </div>
        }

        @if (mode() === 'basic') {
          @if (basicLoading()) {
            <div class="flex items-center gap-3 py-8 text-sm text-muted-foreground">
              <hlm-spinner size="18" /> <span>Loading Basic plan...</span>
            </div>
          } @else if (basicError()) {
            <hlm-alert tone="error">
              <hlm-icon name="lucideCircleAlert" size="1rem" class="mt-0.5 text-destructive" />
              <span>{{ basicError() }}</span>
            </hlm-alert>
          } @else {
            <form [formGroup]="form" class="flex flex-col gap-4">
              <p class="text-sm text-foreground">
                Downgrade from <strong>{{ currentPlanName() }}</strong> to
                <strong>{{ basicPlanName() }}</strong>. All contracts will be updated.
              </p>
              <hlm-form-field>
                <label hlmLabel for="effectiveDate">Change date</label>
                <input id="effectiveDate" hlmInput type="date" formControlName="effectiveDate"
                       [min]="basicMinDateStr()" />
                <p hlmFieldHint>{{ basicDateHint() }}</p>
                @if (form.controls.effectiveDate.hasError('minDate')) {
                  <p hlmFieldError>The downgrade cannot take effect before {{ basicMinDisplay() }}.</p>
                }
              </hlm-form-field>
            </form>
          }
        } @else {
          @if (loading()) {
            <div class="flex items-center gap-3 py-8 text-sm text-muted-foreground">
              <hlm-spinner size="18" />
              <span>Loading cancellation reasons...</span>
            </div>
          } @else if (errorMsg()) {
            <hlm-alert tone="error">
              <hlm-icon name="lucideCircleAlert" size="1rem" class="mt-0.5 text-destructive" />
              <span>{{ errorMsg() }}</span>
            </hlm-alert>
          } @else {
            <form [formGroup]="form" class="flex flex-col gap-5">
              <!-- Who initiated -->
              <div class="flex flex-col gap-2">
                <p class="font-mono-feat text-sm font-semibold uppercase tracking-[0.10em] text-muted-foreground">
                  Who initiated the cancellation?
                </p>
                <hlm-radio-group formControlName="canceledBy" orientation="horizontal">
                  <hlm-radio [value]="0">We</hlm-radio>
                  <hlm-radio [value]="1">Customer</hlm-radio>
                </hlm-radio-group>
              </div>

              <!-- Cancellation reasons -->
              @if (filteredReasons().length > 0) {
                <div class="flex flex-col gap-2">
                  <p class="font-mono-feat text-sm font-semibold uppercase tracking-[0.10em] text-muted-foreground">
                    Cancellation reasons (max 2)
                  </p>
                  <!-- Scrollbox — the reason list is long (old-CMT parity). -->
                  <div class="flex max-h-56 flex-col gap-1.5 overflow-y-auto rounded-lg border border-border p-2">
                    @for (reason of filteredReasons(); track reason.id) {
                      <hlm-checkbox
                        [checked]="isReasonSelected(reason.id)"
                        [disabled]="!isReasonSelected(reason.id) && selectedReasonIds().length >= 2"
                        (checkedChange)="toggleReason(reason.id, $any($event))">
                        {{ reason.text }}
                      </hlm-checkbox>
                    }
                  </div>
                </div>
              }

              <!-- Customer-initiated fields -->
              @if (customerInitiated()) {
                <hlm-form-field>
                  <label hlmLabel for="prevention">Actions to prevent cancellation</label>
                  <textarea id="prevention" hlmTextarea formControlName="prevention"
                            rows="4" maxlength="666"></textarea>
                  <p hlmFieldHint>{{ form.value.prevention?.length || 0 }} / 666</p>
                </hlm-form-field>

                <div class="flex flex-col gap-2">
                  <p class="font-mono-feat text-sm font-semibold uppercase tracking-[0.10em] text-muted-foreground">
                    Customer satisfaction
                  </p>
                  <hlm-radio-group formControlName="satisfaction">
                    <hlm-radio [value]="5">No answer</hlm-radio>
                    <hlm-radio [value]="4">Very will recommend</hlm-radio>
                    <hlm-radio [value]="3">Very</hlm-radio>
                    <hlm-radio [value]="2">Mostly</hlm-radio>
                    <hlm-radio [value]="1">Not at all</hlm-radio>
                  </hlm-radio-group>
                </div>
              }

              <!-- Cancellation date -->
              <hlm-form-field>
                <label hlmLabel for="cancelDate">Cancellation date</label>
                <input id="cancelDate" hlmInput type="date" formControlName="cancelDate"
                       [min]="minDateStr" />
                <p hlmFieldHint>Earliest {{ minDateDisplay }} — one day of back-dating is allowed.</p>
                @if (form.controls.cancelDate.hasError('minDate')) {
                  <p hlmFieldError>The cancellation cannot take effect before {{ minDateDisplay }}.</p>
                }
              </hlm-form-field>

              <!-- Validation hints (old-CMT parity: what still needs filling in) -->
              @if (selectedReasonIds().length === 0 || (customerInitiated() && form.controls.prevention.invalid)) {
                <div class="rounded-lg border border-destructive/40 bg-destructive/5 p-3">
                  <p class="mb-1 text-sm font-semibold text-destructive">Cancellation information is not valid</p>
                  <ul class="flex list-disc flex-col gap-1 pl-5 text-sm text-muted-foreground">
                    @if (selectedReasonIds().length === 0) {
                      <li>Please select the reason of the cancellation. You can select one or two reasons.</li>
                    }
                    @if (customerInitiated() && form.controls.prevention.invalid) {
                      <li>Please describe the actions taken to prevent the cancellation.</li>
                    }
                  </ul>
                </div>
              }
            </form>
          }
        }
      </section>

      <footer hlmDialogFooter>
        <button hlmBtn variant="outline" (click)="dialogRef.close()" [disabled]="submitting()">
          Cancel
        </button>
        @if (mode() === 'basic') {
          <button hlmBtn variant="destructive" (click)="submitBasic()" [disabled]="!canSubmitBasic()">
            @if (submitting()) {
              <hlm-spinner size="16" /> Changing...
            } @else {
              <hlm-icon name="lucideArrowDown" size="0.9rem" /> Send to Basic
            }
          </button>
        } @else {
          <button hlmBtn variant="destructive" (click)="submit()" [disabled]="!canSubmit()">
            @if (submitting()) {
              <hlm-spinner size="16" /> Cancelling...
            } @else {
              <hlm-icon name="lucideTriangleAlert" size="0.9rem" /> Cancel Customer
            }
          </button>
        }
      </footer>
    </hlm-dialog-surface>
  `,
})
export class CancelCustomerDialogComponent implements OnInit {
  readonly data = inject<CancelCustomerDialogData>(MAT_DIALOG_DATA);
  readonly dialogRef = inject(MatDialogRef<CancelCustomerDialogComponent>);
  private readonly contractsService = inject(ContractsService);
  private readonly invoiceService = inject(InvoiceService);
  private readonly authService = inject(AuthService);
  private readonly toast = inject(ToastService);
  private readonly loggingService = inject(LoggingService);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  readonly loading = signal(true);
  readonly errorMsg = signal<string | null>(null);
  readonly submitting = signal(false);
  readonly allReasons = signal<CancellationReason[]>([]);
  readonly selectedReasonIds = signal<number[]>([]);
  /**
   * Earliest cancellation date — old CMT `cancelCustomer.js setMinDate()`:
   * yesterday, raised to the customer's own start date when that is later.
   */
  readonly minDateStr: string;
  readonly minDateDisplay: string;
  /**
   * Earliest Send-to-Basic date. Downgrading to Basic is a plan change in old CMT
   * terms, so it follows `checkDatesForContractChanges()` instead: contract-aware
   * base rule, tightened to `last invoiced period + 1 day` once the invoices load.
   */
  readonly basicMinDateStr = signal('');
  /** Explains the floor that actually applied on the Send-to-Basic date (§17.1). */
  readonly basicDateHint = signal('');
  readonly basicMinDisplay = computed(() => formatDate(this.basicMinDateStr(), 'dd.MM.yyyy', 'en'));
  private readonly basicStartDateStr: string;

  // EB-4005 "Send to Basic" mode state.
  readonly mode = signal<'cancel' | 'basic'>('cancel');
  readonly basicLoading = signal(false);
  readonly basicError = signal<string | null>(null);
  readonly basicPlanName = signal('Basic');
  readonly currentPlanName = signal('');
  private basicPlanId: number | null = null;
  private basicResolved = false;
  get canSendToBasic(): boolean {
    return this.data.mainContractId !== null && this.data.mainContractId !== undefined;
  }

  readonly form: FormGroup = this.fb.group({
    // Default to Customer-initiated (canceledBy=1), matching old CMT.
    canceledBy: [1, Validators.required],
    prevention: [''],
    satisfaction: [5],
    // `[min]` on a native date input adds no validator — these do the real check.
    cancelDate: [null, [Validators.required, minDateValidator(() => this.minDateStr)]],
    // Send-to-Basic date (default: day after the next invoice day)
    effectiveDate: [null, [Validators.required, minDateValidator(() => this.basicMinDateStr())]],
  });

  /** Reactive mirror of canceledBy — a plain computed on form.value is NOT reactive. */
  private readonly canceledBySig = toSignal(this.form.controls.canceledBy.valueChanges, {
    initialValue: this.form.controls.canceledBy.value as number | null,
  });

  /** Reasons for the selected initiator (old-CMT setCorrectReasons: filter by canceledBy). */
  readonly filteredReasons = computed(() => {
    const canceledBy = this.canceledBySig();
    if (canceledBy === null || canceledBy === undefined) return [];
    return this.allReasons().filter((r) => r.canceledBy === canceledBy);
  });

  /** True while the customer-initiated fields (prevention/satisfaction) apply. */
  readonly customerInitiated = computed(() => this.canceledBySig() === 1);

  constructor() {
    // Min date = yesterday, but never before the customer's start date (old CMT
    // cancelCustomer.js setMinDate — AMIE allowed yesterday unconditionally).
    const cancelMin = cancellationMinDate(this.data.customerStartDate);
    this.minDateStr = formatDate(cancelMin, 'yyyy-MM-dd', 'en');
    this.minDateDisplay = formatDate(cancelMin, 'dd.MM.yyyy', 'en');
    const basicMin = contractChangeMinDate({
      contractStartDate: this.data.mainContractStartDate,
      reactivatedStartDate: this.data.mainContractReactivatedStartDate,
      planLevel: this.data.mainContractPlanLevel,
    });
    this.basicMinDateStr.set(formatDate(basicMin, 'yyyy-MM-dd', 'en'));
    // "Send to Basic" starts a new billing arrangement, so old CMT's plan-change
    // default applies: the day after the next invoice day, i.e. the first day of
    // the following billing period (getDefaultStartDate) — not tomorrow (EB-4173).
    // Old CMT relies on its datepicker to refuse an out-of-range date; a native date
    // input does not, so both prefills are raised to their own minimum here.
    this.basicStartDateStr = formatDate(
      atLeastMinDate(defaultStartDate(this.data.invoiceDay), basicMin), 'yyyy-MM-dd', 'en',
    );
    this.form.patchValue({
      effectiveDate: this.basicStartDateStr,
      // Prefill the cancellation date with the customer's next invoice-day boundary
      // (old-CMT getDefaultEndDate parity, EB-3881): this month if today <= invoiceDay,
      // otherwise the invoice day of next month.
      cancelDate: formatDate(
        atLeastMinDate(defaultEndDate(this.data.invoiceDay), cancelMin), 'yyyy-MM-dd', 'en',
      ),
    });
  }

  ngOnInit(): void {
    this.applyInvoicedPeriodRuleToBasicDate();

    this.contractsService.getCancellationReasons(this.data.customerId).subscribe({
      next: (reasons) => {
        this.allReasons.set(reasons);
        this.loading.set(false);
      },
      error: () => {
        this.errorMsg.set('Failed to load cancellation reasons');
        this.loading.set(false);
      },
    });

    // Apply initiator-specific validators for the initial value, then on every change
    // (valueChanges does NOT fire for the initial value).
    this.applyCanceledByValidators(this.form.controls.canceledBy.value);
    this.form.get('canceledBy')!.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: number) => {
        this.selectedReasonIds.set([]);
        this.applyCanceledByValidators(value);
      });

    // EB-4005: open on the recommended "Send to Basic" mode when a main contract
    // exists (this also resolves the Basic plan); otherwise stay on Cancel.
    if (this.canSendToBasic) this.setMode('basic');
  }

  /** prevention + satisfaction are required only for customer-initiated cancellations. */
  private applyCanceledByValidators(value: number): void {
    if (value === 1) {
      this.form.get('prevention')!.setValidators([Validators.required, Validators.maxLength(666)]);
      this.form.get('satisfaction')!.setValidators(Validators.required);
    } else {
      this.form.get('prevention')!.clearValidators();
      this.form.get('satisfaction')!.clearValidators();
      this.form.patchValue({ prevention: '', satisfaction: 5 });
    }
    this.form.get('prevention')!.updateValueAndValidity();
    this.form.get('satisfaction')!.updateValueAndValidity();
  }

  setMode(mode: 'cancel' | 'basic'): void {
    this.mode.set(mode);
    if (mode === 'basic') this.resolveBasic();
  }

  /** Lazily resolve the main contract's current plan + the Basic plan of its price list. */
  private resolveBasic(): void {
    if (this.basicResolved || this.basicLoading() || !this.data.mainContractId) return;
    this.basicLoading.set(true);
    const contractId = this.data.mainContractId;
    this.contractsService.getRevisions(contractId).subscribe({
      next: (revisions) => {
        // Shared with the contract panel's own Change-to-Basic entry point, so the two
        // cannot drift apart again (PR #42 review H4).
        const active = currentOrLatestRevision(revisions);
        if (!active) {
          this.basicError.set('Could not determine the current plan.');
          this.basicLoading.set(false);
          return;
        }
        this.currentPlanName.set(active.productName);
        const priceListId = active.priceListId;
        this.contractsService.getAvailablePlans(contractId).subscribe({
          next: (plans) => {
            const inPriceList = plans.filter((p) => p.priceList.id === priceListId);
            // Basic = the level-5 plan of this price list (old-CMT parity). No name-based
            // fallback: "Basic" is level 1 in some lists and level 5 is "Bare" in others.
            const basic = inPriceList.find((p) => p.level === 5);
            if (!basic) {
              this.basicError.set('No Basic plan available in this price list.');
            } else {
              this.basicPlanId = basic.id;
              this.basicPlanName.set(basic.name ?? 'Basic');
              this.basicResolved = true;
            }
            this.basicLoading.set(false);
          },
          error: () => {
            this.basicError.set('Failed to load available plans');
            this.basicLoading.set(false);
          },
        });
      },
      error: () => {
        this.basicError.set('Failed to load the main contract');
        this.basicLoading.set(false);
      },
    });
  }

  isReasonSelected(id: number): boolean {
    return this.selectedReasonIds().includes(id);
  }

  toggleReason(id: number, checked: boolean): void {
    const current = this.selectedReasonIds();
    if (checked && current.length < 2) {
      this.selectedReasonIds.set([...current, id]);
    } else if (!checked) {
      this.selectedReasonIds.set(current.filter((r) => r !== id));
    }
  }

  canSubmit(): boolean {
    return this.form.get('canceledBy')!.valid
      && this.form.get('cancelDate')!.valid
      && this.form.get('prevention')!.valid
      && this.form.get('satisfaction')!.valid
      && !this.loading()
      && !this.submitting()
      && !this.errorMsg()
      && this.selectedReasonIds().length >= 1;
  }

  canSubmitBasic(): boolean {
    return this.form.get('effectiveDate')!.valid
      && !this.basicLoading()
      && !this.basicError()
      && !this.submitting()
      && this.basicPlanId !== null;
  }

  submit(): void {
    if (!this.canSubmit()) return;
    this.submitting.set(true);

    const cancelDate = this.form.value.cancelDate;
    // Backend expects the full reason objects (not ids), in pick order:
    // mainReason = first selected, subReason = second (optional). See old-CMT parity (EB-3881).
    const selectedReasons = this.selectedReasonIds()
      .map((id) => this.allReasons().find((r) => r.id === id))
      .filter((r): r is CancellationReason => r != null);

    this.contractsService.cancelCustomer(this.data.customerId, cancelDate, {
      prevention: this.form.value.canceledBy === 1 ? this.form.value.prevention : undefined,
      mainReason: selectedReasons[0],
      subReason: selectedReasons[1] ?? undefined,
      satisfaction: this.form.value.canceledBy === 1 ? this.form.value.satisfaction : undefined,
      employeeID: this.authService.currentUserId,
    }).subscribe({
      next: () => {
        /** @ticket EB-3798 — usage: a customer was cancelled with reason(s) (D9). */
        this.loggingService.usage('action.invoke', { action: 'cancel_reason', outcome: 'success' });
        this.toast.open(`Customer cancelled. All contracts end at: ${cancelDate}`, 'OK', { duration: 5000 });
        this.dialogRef.close(true);
      },
      error: () => {
        this.toast.open('Failed to cancel customer', 'OK', { duration: 6000 });
        this.submitting.set(false);
      },
    });
  }

  submitBasic(): void {
    if (!this.canSubmitBasic() || !this.data.mainContractId) return;
    this.submitting.set(true);

    const startDate = this.form.value.effectiveDate;

    this.contractsService.modifyContract(
      this.data.mainContractId!,
      [{ type: 'REPLACE', attribute: 'plan', value: this.basicPlanId! }],
      { startDate, employeeId: String(this.authService.currentUserId) },
    ).subscribe({
      next: () => {
        this.toast.open(`Customer sent to Basic, effective ${startDate}`, 'OK', { duration: 5000 });
        this.dialogRef.close(true);
      },
      error: () => {
        this.toast.open('Failed to send customer to Basic', 'OK', { duration: 6000 });
        this.submitting.set(false);
      },
    });
  }

  /**
   * Send-to-Basic follows old CMT's plan-change rules, so once the customer's
   * invoices are known the date may not fall inside a still-running invoiced
   * period (`contracts.dir.js loadInvoices()` + `checkDatesForContractChanges()`).
   *
   * Old CMT has no Send-to-Basic action of its own; this is the plan-change rule
   * applied to the downgrade, which is what the action does functionally.
   *
   * A failing invoice call leaves the base minimum in place, and a date the
   * operator has already edited is never overwritten.
   */
  private applyInvoicedPeriodRuleToBasicDate(): void {
    if (!this.canSendToBasic) return;

    this.invoiceService.getCurrentInvoices(this.data.customerId).pipe(
      map((invoices) => ({ invoices, failed: false })),
      catchError(() => of({ invoices: [] as Invoice[], failed: true })),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(({ invoices, failed }) => {
      const outcome = contractChangeDateOutcome({
        contractStartDate: this.data.mainContractStartDate,
        reactivatedStartDate: this.data.mainContractReactivatedStartDate,
        planLevel: this.data.mainContractPlanLevel,
        invoiceDay: this.data.invoiceDay,
        maxInvoiceEnd: maxInvoiceEndDate(invoices),
        invoiceLookupFailed: failed,
      });

      this.basicMinDateStr.set(outcome.minDateStr);
      this.basicDateHint.set(outcome.hint);

      const control = this.form.controls['effectiveDate'];
      if (!control.dirty) control.setValue(outcome.prefill);
      control.updateValueAndValidity();
    });
  }

}
